import { Color } from "@mui/material"
import {
  blue,
  blueGrey,
  cyan,
  deepOrange,
  deepPurple,
  lightGreen,
  pink,
  teal
} from "./palette"

export type Colors = {
  text: string
  color: Color
  light: string
  lightHover: string
  initial: string
  initialHover: string
  primary: string
  primaryHover: string
  secondary: string
  secondaryHover: string
  alternate: string
  alternateHover: string
}

type CategoryColors = {
  [category: string]: Colors
}

export type WorkKeysCategories =
  | "appliedmath"
  | "WKAM"
  | "WKAMES"
  | "graphicliteracy"
  | "WKGL"
  | "WKGLES"
  | "workplacedocuments"
  | "WKWD"
  | "WKWDES"
  | "workplaceobservation"
  | "WKWO"
  | "businesswriting"
  | "WKBW"
  | "WKAT"
  | "electricity"
  | "fluiddynamics"
  | "mechanics"
  | "thermodynamics"
  | "WKES"
  | "customerservice"
  | "financialawareness"
  | "interpersonalandbusinesscommunication"
  | "problemsolvingstrategies"
  | "problemsolvingandcriticalthinking"
  | "workdiscipline"
  | "workinginteams"

export const categoryColors: CategoryColors = {
  appliedmath: {
    text: "#fff",
    color: teal,
    light: teal[200],
    lightHover: teal[300],
    initial: teal[300],
    initialHover: teal[400],
    primary: teal[500],
    primaryHover: teal[600],
    secondary: teal[700],
    secondaryHover: teal[800],
    alternate: teal[800],
    alternateHover: teal[900]
  },
  get WKAM() {
    return {
      ...this["appliedmath"]
    }
  },
  get WKAMES() {
    return {
      ...this["appliedmath"]
    }
  },
  // Graphic Literacy
  graphicliteracy: {
    text: "#000",
    color: deepOrange,
    light: deepOrange[200],
    lightHover: deepOrange[300],
    initial: deepOrange[300],
    initialHover: deepOrange[400],
    primary: deepOrange[500],
    primaryHover: deepOrange[600],
    secondary: deepOrange[700],
    secondaryHover: deepOrange[800],
    alternate: deepOrange[800],
    alternateHover: deepOrange[900]
  },
  get WKGL() {
    return {
      ...this["graphicliteracy"]
    }
  },
  get WKGLES() {
    return {
      ...this["graphicliteracy"]
    }
  },
  // Workplace Documents
  workplacedocuments: {
    text: "#000",
    color: cyan,
    light: cyan[200],
    lightHover: cyan[300],
    initial: cyan[300],
    initialHover: cyan[400],
    primary: cyan[500],
    primaryHover: cyan[600],
    secondary: cyan[700],
    secondaryHover: cyan[800],
    alternate: cyan[800],
    alternateHover: cyan[900]
  },
  get WKWD() {
    return {
      ...this["workplacedocuments"]
    }
  },
  get WKWDES() {
    return {
      ...this["workplacedocuments"]
    }
  },
  // Workplace Observation
  workplaceobservation: {
    text: "#000",
    color: lightGreen,
    light: lightGreen[200],
    lightHover: lightGreen[300],
    initial: lightGreen[300],
    initialHover: lightGreen[400],
    primary: lightGreen[500],
    primaryHover: lightGreen[600],
    secondary: lightGreen[700],
    secondaryHover: lightGreen[800],
    alternate: lightGreen[800],
    alternateHover: lightGreen[900]
  },
  get WKWO() {
    return {
      ...this["workplaceobservation"]
    }
  },
  // Business Writing
  businesswriting: {
    text: "#000",
    color: pink,
    light: pink[200],
    lightHover: pink[300],
    initial: pink[300],
    initialHover: pink[400],
    primary: pink[500],
    primaryHover: pink[600],
    secondary: pink[700],
    secondaryHover: pink[800],
    alternate: pink[800],
    alternateHover: pink[900]
  },
  get WKBW() {
    return {
      ...this["businesswriting"]
    }
  },
  // Applied Technology
  WKAT: {
    text: "#000",
    color: blue,
    light: blue[200],
    lightHover: blue[300],
    initial: blue[300],
    initialHover: blue[400],
    primary: blue[500],
    primaryHover: blue[600],
    secondary: blue[700],
    secondaryHover: blue[800],
    alternate: blue[800],
    alternateHover: blue[900]
  },
  get electricity() {
    return {
      ...this["WKAT"]
    }
  },
  get fluiddynamics() {
    return {
      ...this["WKAT"]
    }
  },
  get mechanics() {
    return {
      ...this["WKAT"]
    }
  },
  get thermodynamics() {
    return {
      ...this["WKAT"]
    }
  },
  get problemsolvingstrategies() {
    return {
      ...this["WKAT"]
    }
  },
  // Essential Skills
  WKES: {
    text: "#fff",
    color: deepPurple,
    light: deepPurple[200],
    lightHover: deepPurple[300],
    initial: deepPurple[300],
    initialHover: deepPurple[400],
    primary: deepPurple[500],
    primaryHover: deepPurple[600],
    secondary: deepPurple[700],
    secondaryHover: deepPurple[800],
    alternate: deepPurple[800],
    alternateHover: deepPurple[900]
  },
  get customerservice() {
    return {
      ...this["WKES"]
    }
  },
  get financialawareness() {
    return {
      ...this["WKES"]
    }
  },
  get interpersonalandbusinesscommunication() {
    return {
      ...this["WKES"]
    }
  },
  get problemsolvingandcriticalthinking() {
    return {
      ...this["WKES"]
    }
  },
  get workdiscipline() {
    return {
      ...this["WKES"]
    }
  },
  get workinginteams() {
    return {
      ...this["WKES"]
    }
  }
}

export const getCategoryColors = (category: string): Colors => {
  const findCategory = Object.keys(categoryColors).find(key =>
    category?.includes(key)
  )

  return (
    categoryColors[findCategory] ?? {
      text: "#000",
      color: blueGrey,
      light: blueGrey[200],
      lightHover: blueGrey[300],
      initial: blueGrey[300],
      initialHover: blueGrey[400],
      primary: blueGrey[500],
      primaryHover: blueGrey[600],
      secondary: blueGrey[700],
      secondaryHover: blueGrey[800],
      alternate: blueGrey[800],
      alternateHover: blueGrey[900]
    }
  )
}
